import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import {
  FraternitySharedParent,
  ParentingTestType,
  SameBiologicalMotherAnswer,
} from '@app/modules/service-data/types/paternity.types';
import { TranslateModule } from '@ngx-translate/core';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { controlContainerProvider } from '@shared/helpers';
import { startWith } from 'rxjs';
import {
  RadioFormType,
  ServiceDataFormConditionHistoryComponent,
} from '../service-data-form-condition-history/service-data-form-condition-history.component';
import { ServiceDataFormBirthInformationComponent } from './service-data-form-birth-information/service-data-form-birth-information.component';
import { ServiceDataFormCancerHistoryComponent } from './service-data-form-cancer-history/service-data-form-cancer-history.component';
import { ServiceDataFormCarrierConditionComponent } from './service-data-form-carrier-condition/service-data-form-carrier-condition.component';
import { ServiceDataFormDiseaseHistoryComponent } from './service-data-form-disease-history/service-data-form-disease-history.component';
import { ServiceDataFormRecentPregnancyComponent } from './service-data-form-recent-pregnancy/service-data-form-recent-pregnancy.component';

@Component({
  selector: 'app-service-data-patient-history-information',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ServiceDataFormDiseaseHistoryComponent,
    ServiceDataFormBirthInformationComponent,
    ServiceDataFormCancerHistoryComponent,
    ServiceDataFormConditionHistoryComponent,
    ServiceDataFormCarrierConditionComponent,
    ServiceDataFormRecentPregnancyComponent,
    RequirePermissionDirective,
    SelectComponent,
    SelectOptionComponent,
    ValidationErrorComponent,
  ],
  templateUrl: './service-data-patient-history-information.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataPatientHistoryInformationComponent implements OnInit {
  private controlContainer = inject(ControlContainer);
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) fieldSet: string[] = [];
  @Input() id: string;

  // Paternity: selected in the service information section
  protected readonly parentingTestType = signal<ParentingTestType>(null);
  protected readonly sharedParents = Object.values(FraternitySharedParent);
  protected readonly sameBiologicalMotherAnswers = Object.values(
    SameBiologicalMotherAnswer
  );

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }

  ngOnInit(): void {
    const parentingTestTypeControl = this.form.get(
      'paternity.parentingTestType'
    );
    const fraternityControl = this.form.get('paternity.patientHistory');

    parentingTestTypeControl.valueChanges
      .pipe(
        startWith(parentingTestTypeControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((type: ParentingTestType) => {
        this.parentingTestType.set(type);
        if (type && type !== ParentingTestType.Fraternity) {
          fraternityControl.reset(
            { isSameBiologicalMother: null, wantToKnow: null },
            { emitEvent: false }
          );
        }
      });
  }

  protected readonly RadioFormType = RadioFormType;
  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly ParentingTestType = ParentingTestType;
}
